import type {
  AdCreative,
  MetaAdDraftInput,
  PublishErrorCategory,
} from '../../types/metaAdDraft';
import { targetsAustralia } from '../../lib/metaRegionalRegulation';
import { COMMON_COUNTRIES } from './countries';
import { CUSTOM_EVENT_TYPE_LABELS, OPTIMIZATION_GOAL_LABELS } from '../../lib/metaAdObjectiveShape';
import { hasAnyStoryPosition, placementCounts } from '../../lib/placementMatrix';

// Stage 7A D3 — pure helpers for the Publish UI: the confirmation summary, the
// pre-publish creative warnings, the Meta Ads Manager deep link, and the
// per-category error copy. All pure + unit-tested (no React, no I/O).

export type PublishWarning = { code: string; message: string };

// Mirrors the server's dynamicCreativeActive (publishMapper.ts): does this
// creative publish through Meta's Multiple Text Options shape? The Phase 1
// toggle wins; legacy drafts that predate the flag fall back to the count-derived
// behaviour. Image-only (video multi-text stays first-only).
function dynamicCreativeActive(creative: AdCreative, headlines: string[], texts: string[]): boolean {
  if (creative.assetType !== 'image') return false;
  return (creative.useDynamicCreative as boolean | undefined) ?? (headlines.length > 1 || texts.length > 1);
}

// CLIENT MIRROR of server/modules/metaAds/publishMapper.ts `creativePublishWarnings`.
// Shown in the confirmation dialog BEFORE publish. D3.3 / Phase 1 — image
// multi-text publishes via dynamicCreative when the Dynamic Creative toggle is on,
// so the headline / primary-text truncation warnings apply to VIDEO drafts (video
// multi-text stays first-only) and to image drafts carrying extra variations with
// the toggle OFF. Descriptions now publish on both shapes (2026-06-10 fix) — the
// old descriptions_dropped warning is gone. Keep in sync with the server.
export function creativeWarnings(creative: AdCreative): PublishWarning[] {
  const out: PublishWarning[] = [];
  const headlines = (creative.headlines ?? []).filter((h) => typeof h === 'string' && h.trim() !== '');
  const texts = (creative.primaryTexts ?? []).filter((t) => typeof t === 'string' && t.trim() !== '');
  const truncates = !dynamicCreativeActive(creative, headlines, texts);
  const truncationReason =
    creative.assetType === 'video'
      ? 'multiple text variations aren’t supported on video ads yet. Multi-text video is coming in a future update.'
      : 'enable “Use Dynamic Creative” to publish multiple text variations.';
  // Dedupe: when BOTH headlines + primary texts truncate (the common video case),
  // emit ONE combined note rather than two with an identical explanation.
  const truncatedFields: string[] = [];
  if (truncates && headlines.length > 1) truncatedFields.push('first headline');
  if (truncates && texts.length > 1) truncatedFields.push('first primary text');
  if (truncatedFields.length > 0) {
    out.push({
      code:
        truncatedFields.length > 1
          ? 'creative_text_truncated'
          : truncatedFields[0] === 'first headline'
            ? 'headlines_truncated'
            : 'primary_texts_truncated',
      message: `Only the ${truncatedFields.join(' + ')} will be published — ${truncationReason}`,
    });
  }
  return out;
}

// D3.3 / Phase 1 — Dynamic Creative on Lead Gen + Instant Form. Miki flagged Meta
// may not support dynamic-creative text variations on Instant-Form Lead Gen ads.
// We publish anyway (so the empirical result surfaces), but warn the AM first.
// Fires whenever the dynamicCreative SHAPE will be sent (the toggle is on — even
// a 1+1 draft publishes through it then). No warning for Traffic / Conversions /
// lead_conversion (multi-text fully supported there), nor with the toggle off.
export function multiTextWarnings(input: MetaAdDraftInput): PublishWarning[] {
  const c = input.ads.creative;
  const headlines = (c.headlines ?? []).filter((h) => typeof h === 'string' && h.trim() !== '');
  const texts = (c.primaryTexts ?? []).filter((t) => typeof t === 'string' && t.trim() !== '');
  const isMultiText = dynamicCreativeActive(c, headlines, texts);
  if (
    isMultiText &&
    input.campaign.objective === 'LEADS' &&
    input.ads.destination.type === 'instant_form'
  ) {
    return [
      {
        code: 'leadgen_multitext_unverified',
        message:
          'Lead Gen ads with an Instant Form may not support multiple text variations (Meta limitation). If publish fails, reduce to 1 headline and 1 primary text.',
      },
    ];
  }
  return [];
}

// D3.1 — placement-related publish warnings shown in the confirmation dialog.
// Currently: manual Story placements selected but no Story image → those
// placements get stripped at publish (the mapper console.warns + drops them).
export function placementWarnings(input: MetaAdDraftInput): PublishWarning[] {
  const out: PublishWarning[] = [];
  const pl = input.campaign.placements;
  if (
    pl.mode === 'manual' &&
    pl.manualConfig &&
    hasAnyStoryPosition(pl.manualConfig) &&
    !input.ads.creative.storyAsset.url
  ) {
    out.push({
      code: 'story_placements_without_image',
      message:
        'You’ve selected Story placements but no Story asset (9:16). Those Story placements will be dropped — only Feed placements will run. Add a Story image or video in the Ad section to use them.',
    });
  }
  return out;
}

// Meta Ads Manager deep link for a freshly-published ad. fbAdAccountId is stored
// RAW (no act_) in kyck, but strip a stray prefix defensively. Phase F: verify
// the exact URL shape against a real published ad.
export function metaAdsManagerUrl(fbAdAccountId: string | null, adId: string | null): string | null {
  if (!fbAdAccountId || !adId) return null;
  const act = fbAdAccountId.replace(/^act_/, '');
  return `https://business.facebook.com/adsmanager/manage/ads/edit?act=${encodeURIComponent(
    act,
  )}&selected_ad_ids=${encodeURIComponent(adId)}`;
}

// ---------- confirmation summary ----------

export type PublishSummary = {
  campaignName: string;
  accountName: string;
  budget: string;
  goalDestination: string;
  schedule: string;
  audience: string;
  creative: string;
  placements: string;
  // 'Australian verified advertiser: <id>' only when the publish will send the identity; else null.
  verifiedAdvertiser: string | null;
};

const DATE_FMT = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

function fmtDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return DATE_FMT.format(d);
}

function countryName(code: string): string {
  return COMMON_COUNTRIES.find((c) => c.code === code)?.name ?? code;
}

function genderLabel(g: 'all' | 'male' | 'female'): string {
  return g === 'male' ? 'Men' : g === 'female' ? 'Women' : 'All genders';
}

function pluralize(n: number, noun: string): string {
  return `${n} ${noun}${n === 1 ? '' : 's'}`;
}

// One ad set's audience (geo layer + age + gender + interests), or its extracted-from label.
function oneAdSetAudience(t: MetaAdDraftInput['adSets'][number]['targeting']): string {
  if (t.sourceType === 'extracted') {
    const parts = [t.sourceCampaignName, t.sourceAdSetName].filter(
      (s): s is string => typeof s === 'string' && s.trim() !== '',
    );
    return parts.length > 0 ? `Extracted from ${parts.join(' / ')}` : 'Extracted (locked)';
  }
  let geo: string;
  if (t.cities.length > 0) geo = t.cities.map((c) => c.name).join(', ');
  else if (t.regions.length > 0) geo = t.regions.map((r) => r.name).join(', ');
  else if (t.countries.length > 0) geo = t.countries.map(countryName).join(', ');
  else geo = 'No location set';
  const interests = t.interests.length > 0 ? `, ${pluralize(t.interests.length, 'interest')}` : '';
  return `${geo} · ages ${t.ageMin}–${t.ageMax} · ${genderLabel(t.gender)}${interests}`;
}

// Multi-ad-set: a single ad set summarises inline; multiple list per-ad-set audiences.
function audienceSummary(input: MetaAdDraftInput): string {
  const adSets = input.adSets;
  if (adSets.length === 1) return oneAdSetAudience(adSets[0]!.targeting);
  return `${pluralize(adSets.length, 'ad set')} · ${adSets
    .map((a, i) => `${a.name.trim() || `Ad set ${i + 1}`}: ${oneAdSetAudience(a.targeting)}`)
    .join('  |  ')}`;
}

function goalDestinationSummary(input: MetaAdDraftInput): string {
  const goalLabel = input.campaign.objective === 'LEADS' ? 'Leads' : 'Traffic';
  const dest = input.ads.destination;
  // D2.17 — surface the optimization goal (+ event) so the confirmation reflects it.
  const opt = OPTIMIZATION_GOAL_LABELS[input.campaign.optimizationGoal] ?? input.campaign.optimizationGoal;
  const event =
    input.campaign.optimizationGoal === 'OFFSITE_CONVERSIONS' && input.campaign.customEventType
      ? ` (${CUSTOM_EVENT_TYPE_LABELS[input.campaign.customEventType] ?? input.campaign.customEventType})`
      : '';
  const optPart = ` · optimizing for ${opt}${event}`;
  if (dest.type === 'instant_form') {
    const form = dest.leadGenFormName?.trim() || dest.leadGenFormId || 'instant form';
    return `${goalLabel} via instant form “${form}”${optPart}`;
  }
  return `${goalLabel} to ${dest.url || 'a website'}${optPart}`;
}

// The ad-account currency is a free string (e.g. "AUD"), so mirror PreviewPanel's
// prefix formatting rather than the kyck-Currency-union centsToDisplay.
function formatMoney(cents: number, currency: string | null): string {
  return `${currency ? currency + ' ' : '$'}${(cents / 100).toFixed(2)}`;
}

function budgetSummary(input: MetaAdDraftInput, currency: string | null): string {
  const cbo = input.campaign.budgetMode === 'CBO';
  if (cbo) {
    const budget = input.campaign.budget;
    if (!budget) return 'No budget set · Campaign budget (CBO)';
    const per = budget.type === 'daily' ? '/day' : ' lifetime';
    return `${formatMoney(budget.amountCents, currency)}${per} · Campaign budget (CBO)`;
  }
  // ABO — per ad set. One ad set summarises its budget inline; multiple show a count.
  const adSets = input.adSets;
  if (adSets.length === 1) {
    const budget = adSets[0]!.budget;
    if (!budget) return 'No budget set · Ad-set budget (ABO)';
    const per = budget.type === 'daily' ? '/day' : ' lifetime';
    return `${formatMoney(budget.amountCents, currency)}${per} · Ad-set budget (ABO)`;
  }
  return `Per ad set · ${pluralize(adSets.length, 'ad set')} (ABO)`;
}

function scheduleSummary(input: MetaAdDraftInput): string {
  const start = fmtDate(input.campaign.defaultStartDate);
  const end = fmtDate(input.campaign.defaultEndDate);
  const overrides = input.adSets.filter((a) => a.datesOverridden).length;
  const startPart = start ? `Starting ${start}` : 'Goes live on publish';
  const base = end ? `${startPart}, ending ${end}` : startPart;
  return overrides > 0 ? `${base} · ${overrides} ad set${overrides === 1 ? '' : 's'} override dates` : base;
}

function placementSummary(input: MetaAdDraftInput): string {
  const pl = input.campaign.placements;
  if (pl.mode !== 'manual' || !pl.manualConfig) {
    return 'Automatic (all eligible placements)';
  }
  const { total, feed, story } = placementCounts(pl.manualConfig);
  if (total === 0) return 'No placements selected';
  return `${pluralize(total, 'placement')} (${feed} Feed, ${story} Story)`;
}

function creativeSummary(input: MetaAdDraftInput): string {
  const c = input.ads.creative;
  const headlineN = (c.headlines ?? []).filter((h) => h.trim() !== '').length;
  const primaryN = (c.primaryTexts ?? []).filter((p) => p.trim() !== '').length;
  const descN = (c.descriptions ?? []).filter((d) => d.trim() !== '').length;
  const assetWord = c.assetType === 'video' ? 'video' : 'image';
  const asset = `Feed ${assetWord}`;
  const story = c.storyAsset.url ? `+ Story ${assetWord}` : 'no Story';
  return `${pluralize(headlineN, 'headline')}, ${pluralize(primaryN, 'primary text')}, ${pluralize(
    descN,
    'description',
  )} · ${asset}, ${story}`;
}

// Mirrors the server mapper's gate: an ID is set AND at least one ad set's targeting
// includes Australia (evaluated per ad set, like the publish).
export function verifiedAdvertiserLine(
  input: MetaAdDraftInput,
  id: string | null | undefined,
  accountCountry: string | null | undefined,
): string | null {
  const v = (id ?? '').trim();
  if (v === '') return null;
  const any = input.adSets.some((s) => targetsAustralia(s.targeting as unknown as Record<string, unknown>, accountCountry));
  return any ? `Australian verified advertiser: ${v}` : null;
}

export function buildPublishSummary(
  input: MetaAdDraftInput,
  opts: {
    accountName: string | null;
    currency: string | null;
    verifiedAdvertiserId?: string | null;
    accountCountry?: string | null;
  },
): PublishSummary {
  return {
    campaignName: input.title.trim() || 'Untitled',
    accountName: opts.accountName ?? '—',
    budget: budgetSummary(input, opts.currency),
    goalDestination: goalDestinationSummary(input),
    schedule: scheduleSummary(input),
    audience: audienceSummary(input),
    creative: creativeSummary(input),
    placements: placementSummary(input),
    verifiedAdvertiser: verifiedAdvertiserLine(input, opts.verifiedAdvertiserId, opts.accountCountry),
  };
}

// ---------- per-category error copy ----------

export function publishErrorTitle(category: PublishErrorCategory): string {
  switch (category) {
    case 'auth':
      return 'Meta connection error';
    case 'validation':
      return 'Invalid ad';
    case 'permission':
      return 'Permission denied';
    case 'page_not_paired':
      return 'Page not connected';
    case 'asset_upload':
      return 'Asset upload failed';
    case 'rate_limit':
      return 'Rate limited';
    case 'platform_error':
      return 'Platform error';
    case 'network':
      return 'Network error';
    case 'verified_advertiser_required':
      return 'Verified advertiser needed';
    case 'targeting_rejected':
      return 'Targeting rejected';
    case 'creative_rejected':
      return 'Creative rejected';
    case 'invalid_input_generic':
      return 'Invalid input';
    case 'meta_policy_rejected':
      return 'Policy rejection';
    case 'unknown':
    default:
      return 'Publish failed';
  }
}

// "What to do" guidance per category (spec TASK 5).
export function publishErrorGuidance(category: PublishErrorCategory): string {
  switch (category) {
    case 'verified_advertiser_required':
      return 'Meta needs a verified advertiser for this account’s Australian ads. Add the Meta verified advertiser ID on the account’s Connections tab (Business Settings > Verified identities), then publish again.';
    case 'targeting_rejected':
      return 'Meta rejected the targeting in this ad. Please review the audience section.';
    case 'creative_rejected':
      return 'Meta rejected the creative. Please review images, copy, and ad format.';
    case 'meta_policy_rejected':
      return 'Meta rejected this ad for policy violations. Please review Meta’s advertising policies.';
    case 'invalid_input_generic':
      return 'The request couldn’t be processed. Please contact your administrator.';
    case 'rate_limit':
      return 'Meta is rate-limiting requests. Wait a moment, then retry.';
    case 'network':
      return 'The request didn’t reach Meta. Check your connection and retry.';
    case 'auth':
      return 'The Meta ad account connection has expired or is invalid. Please contact your administrator.';
    case 'permission':
      return 'This ad account is missing a required permission. Please contact your administrator.';
    case 'page_not_paired':
      return 'The Facebook Page isn’t connected to the required resource. Please contact your administrator.';
    case 'asset_upload':
      return 'Meta couldn’t process the creative asset. Try re-uploading the image or video.';
    case 'validation':
      return 'The ad failed validation. Review the highlighted fields and try again.';
    case 'platform_error':
    case 'unknown':
    default:
      return 'Meta’s platform reported an issue. Common causes: account authentication, billing, or account status. Please contact your administrator.';
  }
}
